/**
 * Onboarding — 3 steps (Welcome → Birth data → Privacy).
 *
 * Step 1 (Welcome): hero zodiac wheel + "Карта, рассчитанная с точностью обсерватории."
 * Step 2 (Birth data): name + birth date + birth time + city (autocomplete via searchCities mock).
 * Step 3 (Privacy): plain-language intro to data handling, no spam/push/harvesting.
 * Submit on step 3 → createUser() → redirect to /today.
 *
 * TG WebApp MainButton drives "Продолжить" (bound to current step). Inline button stays for
 * web preview and reduced-motion fallback.
 */
import { useNavigate } from "@tanstack/react-router";
import { type ReactElement, useEffect, useMemo, useState } from "react";

import { ZodiacWheel } from "@/components/ZodiacWheel";
import { type City, createUser, searchCities, type UserCreateRequest } from "@/lib/api";
import { isOnboardingStepValid, type OnboardingStepId } from "@/lib/onboarding-validation";
import { bindMainButton, hapticImpact, hapticNotify } from "@/lib/tg";
import { useTgContext } from "@/state/tg";

type StepId = OnboardingStepId;
const TOTAL_STEPS = 3;

interface BirthForm {
  readonly name: string;
  readonly birthDate: string;
  readonly birthTime: string;
  readonly birthPlace: string;
  readonly city: City | null;
}

const INITIAL_FORM: BirthForm = {
  name: "",
  birthDate: "",
  birthTime: "",
  birthPlace: "",
  city: null,
};

export default function OnboardingPage(): ReactElement {
  const navigate = useNavigate();
  const tg = useTgContext();
  const [step, setStep] = useState<StepId>(1);
  const [form, setForm] = useState<BirthForm>(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stepValid = isOnboardingStepValid(step, form);

  // --- Submit / next-step handler -------------------------------------------
  const advance = useMemo(
    () => async () => {
      if (!stepValid || submitting) return;
      hapticImpact("medium");
      if (step < TOTAL_STEPS) {
        setStep((step + 1) as StepId);
        return;
      }
      // Final step → submit user creation.
      setSubmitting(true);
      setError(null);
      const payload: UserCreateRequest = {
        name: form.name.trim(),
        birthDate: form.birthDate,
        birthTime: form.birthTime.length > 0 ? form.birthTime : null,
        birthPlace: form.city?.name ?? form.birthPlace.trim(),
        timezone: form.city?.timezone ?? null,
        birthLat: form.city?.lat,
        birthLon: form.city?.lon,
      };
      try {
        await createUser(payload, tg.initData);
        hapticNotify("success");
        void navigate({ to: "/", replace: true });
      } catch (err) {
        hapticNotify("error");
        setError(err instanceof Error ? err.message : "Не удалось создать профиль");
        setSubmitting(false);
      }
    },
    [stepValid, submitting, step, form, tg.initData, navigate],
  );

  // --- TG MainButton wiring -------------------------------------------------
  useEffect(() => {
    const label = step < TOTAL_STEPS ? "Продолжить" : "Готово";
    return bindMainButton(label, () => void advance(), { enabled: stepValid && !submitting });
  }, [step, stepValid, submitting, advance]);

  return (
    <main className="screen starry">
      <div className="body-pad ob-screen">
        {step === 1 ? <StepWelcome /> : null}
        {step === 2 ? <StepBirthData form={form} onChange={setForm} /> : null}
        {step === 3 ? <StepPrivacy /> : null}

        <div className="ob-actions">
          {error ? (
            <div className="ob-error" role="alert">
              {error}
            </div>
          ) : null}
          <button
            type="button"
            className="ob-btn"
            disabled={!stepValid || submitting}
            onClick={() => void advance()}
          >
            {submitting ? "Сохраняем…" : step < TOTAL_STEPS ? "Продолжить" : "Готово"}
          </button>
          {step < TOTAL_STEPS ? (
            <button
              type="button"
              className="ob-skip"
              onClick={() => {
                void navigate({ to: "/", replace: true });
              }}
            >
              Пропустить
            </button>
          ) : null}
          <Dots step={step} />
        </div>
      </div>
    </main>
  );
}

/* ----- Step 1: Welcome ----- */

function StepWelcome(): ReactElement {
  return (
    <>
      <div className="ob-illustration">
        <ZodiacWheel />
      </div>
      <div className="ob-content">
        <div className="ob-eyebrow">Шаг 1 из 3</div>
        <h2 className="ob-title">
          Карта, рассчитанная <em>с точностью обсерватории.</em>
        </h2>
        <p className="ob-sub">
          Swiss Ephemeris ±1″. Все 14 планет, узлы, главные неподвижные звёзды. Без упрощений.
        </p>
      </div>
    </>
  );
}

/* ----- Step 2: Birth data ----- */

interface StepBirthDataProps {
  readonly form: BirthForm;
  readonly onChange: (next: BirthForm) => void;
}

function StepBirthData({ form, onChange }: StepBirthDataProps): ReactElement {
  const [cities, setCities] = useState<readonly City[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const needsCitySelection = form.birthPlace.trim().length >= 2 && form.city === null;

  useEffect(() => {
    let cancelled = false;
    const q = form.birthPlace.trim();
    if (q.length < 2) {
      setCities([]);
      return undefined;
    }
    void (async () => {
      try {
        const results = await searchCities(q);
        if (!cancelled) setCities(results);
      } catch {
        if (!cancelled) setCities([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [form.birthPlace]);

  return (
    <div className="ob-content ob-form-wrap">
      <div className="ob-eyebrow">Шаг 2 из 3</div>
      <h2 className="ob-title ob-title--sm">
        Расскажите <em>о себе</em>
      </h2>
      <p className="ob-sub">
        Имя — как обращаться, дата и место — чтобы рассчитать вашу натальную карту.
      </p>

      <form className="ob-form" onSubmit={(e) => e.preventDefault()}>
        <label className="ob-field">
          <span>Имя</span>
          <input
            type="text"
            inputMode="text"
            autoComplete="given-name"
            value={form.name}
            onChange={(e) => onChange({ ...form, name: e.currentTarget.value })}
            placeholder="Алина"
            maxLength={64}
          />
        </label>

        <label className="ob-field">
          <span>Дата рождения</span>
          <input
            type="date"
            value={form.birthDate}
            onChange={(e) => onChange({ ...form, birthDate: e.currentTarget.value })}
          />
        </label>

        <label className="ob-field">
          <span>
            Время рождения <em className="ob-optional">по желанию</em>
          </span>
          <input
            type="time"
            value={form.birthTime}
            onChange={(e) => onChange({ ...form, birthTime: e.currentTarget.value })}
          />
        </label>

        <label className="ob-field" style={{ position: "relative" }}>
          <span>Город рождения</span>
          <input
            type="text"
            autoComplete="off"
            value={form.birthPlace}
            onChange={(e) => {
              onChange({ ...form, birthPlace: e.currentTarget.value, city: null });
              setShowSuggestions(true);
            }}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => {
              // delay so onMouseDown of suggestion fires first
              window.setTimeout(() => setShowSuggestions(false), 120);
            }}
            placeholder="Москва"
          />
          {showSuggestions && cities.length > 0 ? (
            <ul className="ob-suggest">
              {cities.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      // prevent input blur before we can read state
                      e.preventDefault();
                    }}
                    onClick={() => {
                      onChange({ ...form, birthPlace: c.name, city: c });
                      setShowSuggestions(false);
                    }}
                  >
                    <span className="city-name">{c.name}</span>
                    <span className="city-country">{c.country}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {needsCitySelection ? (
            <small className="ob-field-note">
              Выберите город из списка, чтобы сохранить координаты.
            </small>
          ) : null}
        </label>
      </form>
    </div>
  );
}

/* ----- Step 3: Privacy ----- */

function StepPrivacy(): ReactElement {
  return (
    <div className="ob-content">
      <div className="ob-eyebrow">Шаг 3 из 3</div>
      <h2 className="ob-title ob-title--sm">
        Тихо, без <em>спама и шума.</em>
      </h2>
      <p className="ob-sub">
        Только важные транзиты и только если вы сами включите уведомления. Ваши данные хранятся
        зашифрованными, не продаются и не используются для рекламы.
      </p>
      <ul className="ob-privacy">
        <li>
          <span aria-hidden="true">·</span> Без push по умолчанию
        </li>
        <li>
          <span aria-hidden="true">·</span> Без рассылок и трекеров
        </li>
        <li>
          <span aria-hidden="true">·</span> Карту можно удалить в один тап
        </li>
      </ul>
    </div>
  );
}

/* ----- Pagination dots ----- */

function Dots({ step }: { readonly step: StepId }): ReactElement {
  return (
    // biome-ignore lint/a11y/useSemanticElements: progress dots UI; fieldset has user-agent styling that breaks the .ob-dots layout
    <div className="ob-dots" role="group" aria-label={`Шаг ${step} из ${TOTAL_STEPS}`}>
      {Array.from({ length: TOTAL_STEPS }, (_, i) => (
        <span key={i} className={`ob-dot${i + 1 === step ? " active" : ""}`} />
      ))}
    </div>
  );
}
